/**
 * Đề xuất sửa Hộ / Vườn / MM từ thực địa (Doc/11a E2, Doc/03 mục 3c) — phần logic thuần,
 * không DOM, không gọi API, để kiểm được trong môi trường `node` của vitest.
 *
 * Danh sách trường PHẢI khớp `PROPOSABLE_FIELDS` ở
 * `apps/api/src/modules/master-data/change-request-fields.ts` — test
 * `proposal-fields.test.js` so hai bên. Trường nằm ngoài danh sách máy chủ trả
 * `FIELD_NOT_PROPOSABLE`; trường máy chủ nhận mà giao diện không hiện thì điều tra viên
 * không có đường báo sai.
 */
import { SOIL_TYPES, MIDDLEMAN_TYPES, MIDDLEMAN_TYPE_LABELS } from '@farmer/audit-rules';

/** Mã lý do của máy chủ (`REASON_CODES`) → chip trên giao diện. */
export const REASONS = [
  ['typo', 'Sai chính tả'],
  ['changed', 'Đã thay đổi'],
  ['remeasured', 'Đo lại tại vườn'],
  ['source_wrong', 'Dữ liệu nguồn sai'],
  ['added', 'Bổ sung'],
  ['other', 'Khác'],
];

export const ENTITY_LABEL = { household: 'Hộ', plot: 'Vườn', middleman: 'Đại lý/MM' };

/**
 * `kind`: text | decimal | enum | gps. `gps` là một hàng giao diện cho HAI trường máy
 * chủ (`gpsLat`, `gpsLng`) — tách hai hàng thì nút "Lấy toạ độ hiện tại" chỉ điền được
 * một nửa. `audit`: trường bộ luật đối chiếu đọc (Q1 — phiếu nháp tính theo giá trị đề xuất).
 */
export const PROPOSAL_FIELDS = {
  household: [
    { key: 'fullName', label: 'Họ tên chủ hộ', kind: 'text', required: true },
    { key: 'phone', label: 'Số điện thoại', kind: 'text', inputmode: 'tel' },
    { key: 'idCardNumber', label: 'CCCD', kind: 'text', inputmode: 'numeric' },
    { key: 'farmerCode', label: 'Mã nông dân (IDH)', kind: 'text' },
    { key: 'addressDetail', label: 'Địa chỉ chi tiết', kind: 'text' },
    { key: 'gps', label: 'Toạ độ nhà', kind: 'gps' },
  ],
  plot: [
    { key: 'areaHa', label: 'Diện tích (ha)', kind: 'decimal', required: true, audit: true, inputmode: 'decimal' },
    { key: 'soilType', label: 'Thành phần đất', kind: 'enum', options: SOIL_TYPES.map((v) => [v, v]), required: true, audit: true },
    { key: 'gps', label: 'Toạ độ vườn', kind: 'gps' },
  ],
  middleman: [
    { key: 'name', label: 'Tên đại lý / MM', kind: 'text', required: true },
    { key: 'type', label: 'Loại', kind: 'enum', options: MIDDLEMAN_TYPES.map((v) => [v, MIDDLEMAN_TYPE_LABELS[v]]), required: true },
    { key: 'addressDetail', label: 'Địa chỉ chi tiết', kind: 'text' },
  ],
};

/** Trường máy chủ mà một hàng giao diện chạm tới. */
export function serverFieldsOf(field) {
  return field.kind === 'gps' ? ['gpsLat', 'gpsLng'] : [field.key];
}

/** Mọi tên trường máy chủ của một loại bản ghi — để so với API. */
export function proposableServerFields(entity) {
  return PROPOSAL_FIELDS[entity].flatMap(serverFieldsOf);
}

const FIELD_LABEL = {
  gpsLat: 'Vĩ độ',
  gpsLng: 'Kinh độ',
};

/** Nhãn của một trường máy chủ, cho danh sách đề xuất ("Diện tích (ha): 1,5 → 1,6"). */
export function fieldLabel(entity, serverField) {
  if (FIELD_LABEL[serverField]) return FIELD_LABEL[serverField];
  return PROPOSAL_FIELDS[entity]?.find((f) => f.key === serverField)?.label || serverField;
}

/** Giá trị hiển thị: rỗng → `null` để nơi gọi tự viết "Chưa có"; loại MM → nhãn tiếng Việt. */
export function displayValue(entity, serverField, value) {
  if (value === undefined || value === null || String(value).trim() === '') return null;
  if (entity === 'middleman' && serverField === 'type') return MIDDLEMAN_TYPE_LABELS[value] || String(value);
  return String(value);
}

/** "1,6" hay "1.60" đều là 1.6 — so như máy chủ (`normalizeProposedValue`). */
function sameValue(kind, a, b) {
  const sa = String(a ?? '').trim();
  const sb = String(b ?? '').trim();
  if (kind === 'decimal' || kind === 'gps') {
    if (sa === '' || sb === '') return sa === sb;
    return Number(sa.replace(',', '.')) === Number(sb.replace(',', '.'));
  }
  return sa === sb;
}

/**
 * Trạng thái các hàng → `items` của `POST /change-requests`, hoặc `{ error }` nói rõ
 * hàng nào sai để hiện ngay trong sheet (đỡ một vòng gửi lên máy chủ rồi nhận 422).
 *
 * @param {'household'|'plot'|'middleman'} entity
 * @param {Record<string, unknown>} record  bản ghi hiện hành
 * @param {Record<string, {diff: boolean, value?: string, lat?: string, lng?: string, reason?: string, note?: string}>} rows
 */
export function buildProposalItems(entity, record, rows) {
  const items = [];
  for (const field of PROPOSAL_FIELDS[entity]) {
    const row = rows[field.key];
    if (!row?.diff) continue;
    const label = `“${field.label}”`;

    const changes = [];
    if (field.kind === 'gps') {
      const lat = String(row.lat ?? '').trim();
      const lng = String(row.lng ?? '').trim();
      if (!lat || !lng) return { error: `${label}: nhập đủ vĩ độ và kinh độ.` };
      const nLat = Number(lat.replace(',', '.'));
      const nLng = Number(lng.replace(',', '.'));
      if (!Number.isFinite(nLat) || Math.abs(nLat) > 90) return { error: `${label}: vĩ độ phải là số từ -90 đến 90.` };
      if (!Number.isFinite(nLng) || Math.abs(nLng) > 180) return { error: `${label}: kinh độ phải là số từ -180 đến 180.` };
      if (!sameValue('gps', lat, record.gpsLat)) changes.push(['gpsLat', lat]);
      if (!sameValue('gps', lng, record.gpsLng)) changes.push(['gpsLng', lng]);
    } else {
      const value = String(row.value ?? '').trim();
      if (!value && field.required) return { error: `${label}: không để trống được.` };
      if (field.kind === 'decimal' && value) {
        const n = Number(value.replace(',', '.'));
        if (!Number.isFinite(n) || n < 0.01) return { error: `${label}: phải là số từ 0,01 trở lên.` };
      }
      if (!sameValue(field.kind, value, record[field.key])) changes.push([field.key, value || null]);
    }

    if (!changes.length) return { error: `${label}: giá trị mới đang giống giá trị cũ.` };
    if (!row.reason) return { error: `${label}: chọn lý do.` };
    const note = String(row.note ?? '').trim();
    if (row.reason === 'other' && !note) return { error: `${label}: lý do “Khác” cần ghi chú.` };

    for (const [serverField, newValue] of changes) {
      items.push({ field: serverField, newValue, reasonCode: row.reason, ...(note ? { reasonNote: note } : {}) });
    }
  }
  return { items };
}

/**
 * Giá trị đang chờ duyệt theo từng trường máy chủ, gộp đề xuất trên máy chủ và đề xuất
 * còn nằm trong hàng đợi (`status: 'queued'`). Mỗi trường có tối đa MỘT đề xuất chờ —
 * máy chủ chặn đề xuất thứ hai (Doc/11 mục 6), nên không có gì để chọn giữa hai giá trị.
 *
 * @returns {Map<string, {value: string|null, changeRequestId?: string, status: 'pending'|'queued'}>}
 */
export function pendingByField(changeRequests) {
  const out = new Map();
  for (const cr of changeRequests || []) {
    if (cr.action !== 'update') continue;
    for (const it of cr.items || []) {
      out.set(it.field, { value: it.newValue ?? null, changeRequestId: cr.id, status: cr.status === 'queued' ? 'queued' : 'pending' });
    }
  }
  return out;
}

/** Có đề xuất ngừng hoạt động đang chờ không — để không cho gửi cái thứ hai. */
export function hasPendingDeactivate(changeRequests) {
  return (changeRequests || []).some((cr) => cr.action === 'deactivate');
}

/** Mã đề xuất ngắn cho người đọc: 8 ký tự đầu của UUID là đủ để nói chuyện qua điện thoại. */
export function shortId(id) {
  return id ? String(id).slice(0, 8).toUpperCase() : '';
}

/** Khoá idempotency cho hàng đợi offline (Q4). DTO đòi 8–100 ký tự. */
export function newClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `cr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
